import { ReactNode, useEffect, useRef } from "react";
import { createPortal } from "react-dom";

import Button from "components/Button";
import { XCloseIcon } from "components/icons";

import cn from "lib/cn";

export interface ModalDialogProps {
  id?: string;
  open: boolean;
  cancelLabel?: string;
  submitLabel?: string;
  className?: string;
  text?: React.ReactNode;
  loading?: boolean;
  loadingCancel?: boolean;
  icon?: ReactNode;
  disableClose?: boolean;
  title?: string;
  submitDisabled?: boolean;
  submitBtnColor?: "primary" | "lightGray" | "red" | "mustard";
  cancelBtnColor?: "primary" | "lightGray" | "red" | "mustard";
  isPadding?: boolean;
  hideCancelButton?: boolean;
  hideSubmitButton?: boolean;
  contentClassName?: string;
  titleClassName?: string;
  containerClassName?: string;
  onCancel?: () => void;
  onDiscard?: () => void;
  onSubmit?: () => void;
}

const ModalDialog = ({
  id,
  open,
  submitLabel = "Yes",
  cancelLabel,
  text,
  className,
  loading,
  loadingCancel,
  icon,
  disableClose,
  title,
  submitBtnColor,
  cancelBtnColor,
  submitDisabled,
  isPadding,
  hideCancelButton = false,
  hideSubmitButton = false,
  contentClassName,
  titleClassName,
  containerClassName,
  onCancel,
  onDiscard,
  onSubmit,
}: ModalDialogProps) => {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (open) {
        modalRef?.current?.focus();
      }
    }, 300);

    return () => {
      clearTimeout(timeout);
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      id={id}
      data-testid={id}
      className={cn(
        "portal-elm w-full h-screen z-[999] fixed bg-black/50 top-0 flex",
        containerClassName
      )}
    >
      <div
        tabIndex={-1}
        ref={modalRef}
        autoFocus
        data-testid={id ? `content-${id}` : undefined}
        className={cn([
          "w-full absolute max-h-[80%] -translate-y-[50%] max-w-[24rem] -translate-x-[50%] bg-white left-1/2 top-1/2 rounded-lg p-5 overflow-auto",
          className,
        ])}
      >
        {!disableClose && (
          <div className="relative w-full">
            <div
              className={cn(
                "absolute right-0 top-2 cursor-pointer",
                loading && "cursor-wait"
              )}
              onClick={loading ? undefined : onCancel}
            >
              <XCloseIcon
                width={14}
                height={14}
                data-testid={id ? `closeIcon-${id}` : undefined}
              />
            </div>
          </div>
        )}

        <div className={cn("pt-3", !isPadding && "p-0")}>{icon}</div>

        <div
          className={cn(
            "pt-8 pb-1 mx-auto flex flex-col space-y-3",
            !isPadding && "p-0"
          )}
        >
          <div
            className={cn(
              "text-left font-semibold text-lg pr-8",
              titleClassName
            )}
          >
            {title}
          </div>
          <div className={cn("text-center text-base pb-4", contentClassName)}>
            {text}
          </div>

          <div className="flex justify-center space-x-4 w-full">
            {(!!onCancel || !!onDiscard) && !hideCancelButton && (
              <Button
                className="w-full text-base"
                onClick={onDiscard || onCancel}
                variant="outlined"
                color={cancelBtnColor || "lightGray"}
                isLoading={loadingCancel}
                disabled={loading || loadingCancel}
              >
                {cancelLabel ? cancelLabel : "No"}
              </Button>
            )}

            {!hideSubmitButton && (
              <Button
                className="w-full text-base"
                onClick={onSubmit}
                variant="contained"
                color={submitBtnColor}
                isLoading={loading}
                disabled={submitDisabled}
              >
                {loading ? "" : submitLabel}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ModalDialog;
